import {LegacyWBIcon} from '@wandb/common/components/elements/LegacyWBIcon';
import {
  isMediaCardType,
  MediaCardType,
  mediaCardTypeToKeys,
} from '@wandb/common/types/media';
import DOC_URLS, {lookupDocUrl} from '@wandb/common/util/doc_urls';
import {ID} from '@wandb/common/util/id';
import _ from 'lodash';
import React, {useCallback} from 'react';

import {useKeyInfoQuery} from '../state/runs/hooks';
import * as PanelViewTypes from '../state/views/panel/types';
import * as RunSetViewTypes from '../state/views/runSet/types';
import * as Panels from '../util/panels';
import * as RunHelpers from '../util/runhelpers';
import {useBetaFeatureVega3Enabled} from '../util/useBetaFeature';
import {useFeatureFlagVegaEnabled} from '../util/useFeatureFlag';
import * as PanelVega2 from './PanelVega2';
import {useRecommendedWeavePanels} from './RecommendedWeavePanels';

const BASIC_PANELS = [
  {
    name: 'Line plot',
    type: 'line',
    icon: 'panel-line-plot',
  },
  {
    name: 'Bar chart',
    type: 'bar',
    icon: 'panel-bar-chart',
  },
  {
    name: 'Scatter plot',
    type: 'scatter',
    icon: 'panel-scatter-plot',
    hideForSingleRun: true,
  },
  {
    name: 'Parallel coordinates',
    type: 'parallel',
    icon: 'panel-parallel',
    hideForSingleRun: true,
  },
  {
    name: 'Scalar chart',
    type: 'scalar',
    icon: 'panel-number',
  },
  {
    name: 'Run comparer',
    type: 'comparer',
    icon: 'panel-run-comparer',
    hideForSingleRun: true,
  },
  {
    name: 'Parameter importance',
    type: 'parameter-importance',
    icon: 'panel-importance',
    hideForSingleRun: true,
  },
  {
    name: 'Code',
    type: 'code',
    icon: 'panel-html',
  },
  {
    name: 'Markdown',
    type: 'markdown',
    icon: 'panel-text',
  },
];

const GALLERY_PANELS = [
  {
    name: 'Images',
    type: 'image',
    icon: 'panel-images',
  },
  {
    name: 'Plotly',
    type: 'plotly',
    icon: 'panel-plotly',
  },
  {
    name: '3D objects',
    type: 'object3D',
    icon: 'panel-3d',
  },
  {
    name: 'Video',
    type: 'video',
    icon: 'panel-video',
  },
  {
    name: 'Audio',
    type: 'audio',
    icon: 'panel-audio',
  },
  {
    name: 'HTML',
    type: 'html',
    icon: 'panel-html',
  },
  {
    name: 'Text table',
    type: 'table',
    icon: 'panel-text-table',
  },
];

interface AddVisMenuProps {
  entityName: string;
  singleRun?: boolean;
  runSetRefs: RunSetViewTypes.Ref[];
  onSelect(panel: PanelViewTypes.Panel, skipEdit?: boolean): void;
}

type AllAddVisMenuProps = AddVisMenuProps &
  ReturnType<typeof useAddVisMenuProps>;

const AddVisMenuComp: React.FC<AllAddVisMenuProps> = React.memo(
  ({
    singleRun,
    onSelect,
    keyInfoQuery,
    vegaPanelEnabled,
    vega3PanelEnabled,
  }) => {
    const keysOfType = useCallback(
      (type: string): string[] | undefined => {
        if (keyInfoQuery.loading || keyInfoQuery.error != null) {
          return undefined;
        }
        const {historyKeyInfo} = keyInfoQuery;
        if (historyKeyInfo == null) {
          return [];
        }
        const kt = RunHelpers.keyTypes(historyKeyInfo);
        const typesToKeys: {
          [index: string]: string[];
        } = {};
        _.map(kt, (t, key) => {
          if (typesToKeys[t] == null) {
            typesToKeys[t] = [];
          }
          typesToKeys[t].push(key);
        });

        if (['line', 'scatter', 'parallel', 'bar'].indexOf(type) > -1) {
          type = 'number';
        }

        return typesToKeys[type];
      },
      [keyInfoQuery]
    );

    const recommendedWeavePanels = useRecommendedWeavePanels(keysOfType);

    const classNames = useCallback(
      (type: string): string => {
        if (type.startsWith('custom:')) {
          return ['enabled', 'legacy-vega'].join(' ');
        }
        // Always enable parallel and scatter. They work as long as there is more than one run.
        // Note from John: why would you make this function instead of just inlining it?
        if (
          type === 'markdown' ||
          type === 'parallel' ||
          type === 'scatter' ||
          type === 'panel-weave' ||
          type === 'legacy-vega' ||
          type === 'vega2' ||
          type === 'vega3' ||
          type === 'parameter-importance' ||
          type === 'comparer' ||
          type === 'scalar' ||
          type === 'code' // TODO: only enable when we have code
        ) {
          return ['enabled', type].join(' ');
        }

        if (recommendedWeavePanels[type] != null) {
          return [
            recommendedWeavePanels[type].enabled ? 'enabled' : 'disabled',
            type,
          ].join(' ');
        }

        const keyList = isMediaCardType(type)
          ? mediaCardTypeToKeys(type as MediaCardType)
          : [type];

        for (const t of keyList) {
          if (keysOfType(t)) {
            return ['enabled', type].join(' ');
          }
        }

        return ['disabled', type].join(' ');
      },
      [keysOfType, recommendedWeavePanels]
    );

    const openEditor = useCallback(
      (type: string) => {
        const isDisabled = classNames(type).includes('disabled');
        trackAddPanel(type, isDisabled);
        // For disabled cards link to docs instead of
        // launching editor
        if (isDisabled) {
          window.open(lookupDocUrl(type) ?? DOC_URLS.logs, '_blank'); // eslint-disable-line wandb/no-unprefixed-urls
          return;
        }

        if (type.startsWith('custom:')) {
          if (keyInfoQuery.loading || keyInfoQuery.error != null) {
            return;
          }
          const key = type.substring(7);
          const {id, ...props} = keyInfoQuery.viz[key];
          const panelDefId =
            id != null && !(id.startsWith('builtin:') || id.startsWith('lib:'))
              ? `lib:${id}`
              : id;
          onSelect(
            {
              __id__: ID(),
              viewType: 'Vega',
              config: {
                panelDefId,
                ...props,
              },
            } as PanelViewTypes.Panel,
            true
          );
          return;
        }
        // LB: why not check if the card is disabled instead?
        let viewType: Panels.PanelType = 'Run History Line Plot';
        // We could type this if we made a function that returns a panel type
        // and valid config
        let config: any = {};
        if ((type === 'table' || type === 'table-file') && !singleRun) {
          // We use the multi run table if we're in a multiple run context. This is
          // maybe not ideal? The user may want the media browser. Maybe we let the
          // user pick table and then have a second modal where they can pick between
          // the "Gallery" (media browser) and multi run table.
          viewType = 'Multi Run Table';
        } else if (isMediaCardType(type)) {
          viewType = 'Media Browser';
        } else if (type === 'parallel') {
          viewType = 'Parallel Coordinates Plot';
        } else if (type === 'scatter') {
          viewType = 'Scatter Plot';
        } else if (type === 'panel-weave') {
          viewType = 'Weave';
        } else if (type === 'histogram') {
          viewType = 'Run History Line Plot';
        } else if (type === 'markdown') {
          viewType = 'Markdown Panel';
        } else if (type === 'scalar') {
          viewType = 'Scalar Chart';
        } else if (type === 'legacy-vega') {
          viewType = 'Vega';
        } else if (type === 'vega2') {
          viewType = 'Vega2';
          config = PanelVega2.INITIAL_CONFIG;
        } else if (type === 'vega3') {
          viewType = 'Vega3';
        } else if (type === 'parameter-importance') {
          viewType = 'Parameter Importance';
        } else if (type === 'comparer') {
          viewType = 'Run Comparer';
        } else if (type === 'code') {
          viewType = 'Code Comparer';
        } else if (type === 'bar') {
          viewType = 'Bar Chart';
        } else if (type === 'line') {
          viewType = 'Run History Line Plot';
        } else if (recommendedWeavePanels[type] != null) {
          viewType = 'Weave';
          config = recommendedWeavePanels[type].defaultConfig;
        }
        onSelect({
          __id__: ID(),
          viewType,
          config,
        } as PanelViewTypes.Panel);
      },
      [classNames, keyInfoQuery, onSelect, singleRun, recommendedWeavePanels]
    );

    const advancedPanels: Array<{
      name: string;
      type: string;
      icon: string;
    }> = [];

    if (vegaPanelEnabled) {
      advancedPanels.push({
        name: 'Vega',
        type: 'legacy-vega',
        icon: 'panel-line-plot',
      });
    }

    advancedPanels.push({
      name: 'Custom chart',
      type: 'vega2',
      icon: 'panel-line-plot',
    });

    if (vega3PanelEnabled) {
      advancedPanels.push({
        name: 'Vega 3',
        type: 'vega3',
        icon: 'panel-line-plot',
      });
    }

    advancedPanels.push({
      name: 'Weave',
      type: 'panel-weave',
      icon: 'search',
    });

    const basicPanels = singleRun
      ? BASIC_PANELS.filter(p => !p.hideForSingleRun)
      : BASIC_PANELS;
    const column1 = _.concat(basicPanels, advancedPanels);
    const column2 = GALLERY_PANELS;

    return (
      <>
        <div className="wb-add-vis-menu">
          <ul className="add-panel-list right-border">
            {column1.map((item, i) => (
              <li
                data-test={'add-panel-' + item.type}
                key={`col1-panel-option-${i}`}
                className={classNames(item.type)}
                onClick={(e: any) => {
                  openEditor(item.type);
                }}>
                <LegacyWBIcon name={item.icon} />
                {item.name}
              </li>
            ))}
          </ul>
          <ul className="add-panel-list right-border">
            {column2.map((item, i) => (
              <li
                data-test={'add-panel-' + item.type}
                key={`col2-panel-option-${i}`}
                className={classNames(item.type)}
                onClick={(e: any) => {
                  openEditor(item.type);
                }}>
                <LegacyWBIcon name={item.icon} />
                {item.name}
              </li>
            ))}
          </ul>
          <ul className="add-panel-list">
            {Object.entries(recommendedWeavePanels).map(([type, panel], i) => (
              <li
                data-test={'add-panel-' + type}
                key={`col3-panel-option-${i}`}
                className={classNames(type)}
                onClick={(e: any) => {
                  openEditor(type);
                }}>
                <LegacyWBIcon name={panel.icon} />
                {panel.name}
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }
);

const trackAddPanel = (panelTypeId: string, disabled: boolean) => {
  window.analytics?.track('Add Panel From Menu', {
    panelTypeId,
    disabled,
  });
};

function useAddVisMenuProps(props: AddVisMenuProps) {
  const {entityName, runSetRefs} = props;
  const keyInfoQuery = useKeyInfoQuery(runSetRefs);

  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);
  const vega3PanelEnabled = useBetaFeatureVega3Enabled();

  return {
    keyInfoQuery,
    vegaPanelEnabled,
    vega3PanelEnabled,
  };
}

const AddVisMenu = (props: AddVisMenuProps) => {
  const selectedProps = useAddVisMenuProps(props);
  return <AddVisMenuComp {...props} {...selectedProps} />;
};

export default AddVisMenu;
